/**
 * usePlanningAssumptions — Phase A shared planning store (Zustand).
 *
 * Single source of truth for MC / FIRE / drawdown / scenarios. Pages read
 * defaults from this store; slider changes debounce-write through `patch()`.
 *
 * The store mirrors `/api/profile/assumptions` (resolved view): every field
 * is always populated, never null at the top level. `null` only appears
 * inside individual `property_plans` entries (e.g. no sale year set yet).
 */
import { create } from 'zustand'

import {
  getPlanningAssumptions,
  patchPlanningAssumptions,
} from '@/lib/api'
import type {
  PlanningAssumptions,
  PlanningAssumptionsPatch,
} from '@/lib/types'

const DEBOUNCE_MS = 500

interface PlanningAssumptionsState {
  assumptions: PlanningAssumptions | null
  loading: boolean
  saving: boolean
  error: string | null

  /** Fetch the resolved view once (or refresh). */
  fetch: () => Promise<void>

  /** Apply a partial patch immediately (no debounce). Returns the resolved
   *  view echoed back from the server, or null on error. */
  patch: (body: PlanningAssumptionsPatch) => Promise<PlanningAssumptions | null>

  /** Apply a partial patch with a debounce — useful for slider drags so
   *  we don't fire a request per pixel. The optimistic local state updates
   *  immediately so the UI feels instant. */
  patchDebounced: (body: PlanningAssumptionsPatch) => void
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let pendingPatch: PlanningAssumptionsPatch = {}

export const usePlanningAssumptions = create<PlanningAssumptionsState>()(
  (set, get) => ({
    assumptions: null,
    loading: false,
    saving: false,
    error: null,

    fetch: async () => {
      set({ loading: true, error: null })
      try {
        const assumptions = await getPlanningAssumptions()
        set({ assumptions, loading: false })
      } catch (e) {
        set({ error: String(e), loading: false })
      }
    },

    patch: async (body) => {
      // Optimistic update — merge into local state right away so consumers
      // re-render before the network round-trip completes.
      const current = get().assumptions
      if (current) {
        set({ assumptions: { ...current, ...body } as PlanningAssumptions })
      }
      set({ saving: true, error: null })
      try {
        const assumptions = await patchPlanningAssumptions(body)
        set({ assumptions, saving: false })
        return assumptions
      } catch (e) {
        set({ error: String(e), saving: false })
        return null
      }
    },

    patchDebounced: (body) => {
      // Coalesce subsequent calls into a single payload, optimistic-merge
      // immediately so sliders feel snappy. Field overwrite semantics
      // match the server-side PATCH (last value wins).
      pendingPatch = { ...pendingPatch, ...body }

      const current = get().assumptions
      if (current) {
        set({ assumptions: { ...current, ...body } as PlanningAssumptions })
      }

      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        const toSend = pendingPatch
        pendingPatch = {}
        debounceTimer = null
        void get().patch(toSend)
      }, DEBOUNCE_MS)
    },
  }),
)
